<template>
  <!-- Hidden until /me resolves, and for good if it fails — as the pre-migration modal was. -->
  <v-dialog
    :model-value="!!me"
    persistent
    max-width="650"
    data-cy="trust-deposit-dialog"
  >
    <!-- Grey uppercase labels and the 32px gutter reproduce the legacy `.ts .modal` look this
         dialog had before it moved to v-dialog (src/sass/_forms.sass, _modal.sass). -->
    <v-card
      class="tw text-neutral-700 [&_label]:uppercase [&_label]:text-neutral-500 [&_label]:text-sm"
    >
      <v-card-title
        class="d-flex align-center justify-space-between pt-5 pl-8 pr-4"
      >
        <span
          v-if="!uiState.loading"
          class="text-h6 font-weight-bold"
          data-cy="deposit-title"
        >
          Record Deposit to Trust Account
        </span>
        <!-- close-btn: the trust Cypress suite (functional-tests) closes the dialog by it. -->
        <LlSecondaryButton
          class="close-btn ml-auto"
          icon="mdi-close"
          variant="text"
          size="small"
          aria-label="Close"
          data-cy="deposit-close"
          @click="cancel"
        />
      </v-card-title>

      <v-card-text v-if="uiState.loading" class="px-8 pb-6">
        <v-skeleton-loader
          type="paragraph, list-item@4, actions"
          elevation="0"
          data-cy="deposit-loading"
        />
      </v-card-text>

      <v-card-text v-else class="px-8 pt-2 text-base">
        <!-- Opened from the Settlement tab the matter is already in context. -->
        <div
          v-if="!uiState.pick && !settlementContext"
          data-cy="deposit-context"
        >
          <h5>{{ formState.matter?.name }}</h5>
          <h4>{{ formState.client?.name }}</h4>
        </div>

        <!-- v-if, not v-show: mounting the picker is what issues the /options queries, so the
             routes that already know the client/matter must not mount it at all. -->
        <div v-if="uiState.pick">
          <!-- client-select / matter-select are the hooks the trust Cypress suite drives. -->
          <div class="py-2" data-cy="client-matter-search">
            <ClientMatterSearch
              :cm="cm"
              :query-filter="cmQuery"
              :horizontal="true"
              :client-only="isClientLevel"
              client-data-cy="client-select"
              matter-data-cy="matter-select"
            />
            <div v-if="isClientLevel" class="pt-2" data-cy="client-level-note">
              <i>Deposit is done at client level</i>
            </div>
          </div>
          <div
            v-if="showTrustAccount"
            class="flex py-2"
            data-cy="trust-account-field"
          >
            <div class="w-3/12">
              <!-- Bound by for/id; the rule only recognises a native control nested inside. -->
              <!-- eslint-disable-next-line vuejs-accessibility/label-has-for -->
              <label for="deposit-trust-account" class="py-2"
                >Trust Account</label
              >
            </div>
            <div class="w-9/12">
              <LlSelect
                id="deposit-trust-account"
                v-model="formState.trust"
                :items="trusts"
                item-title="label"
                item-value="id"
                placeholder="Select Trust Account"
                variant="outlined"
                density="compact"
                hide-details="true"
                return-object
                label-class="!hidden"
                data-cy="trust-account-select"
              />
            </div>
          </div>
        </div>

        <span
          v-if="firstDeposit"
          class="text-yellow-600"
          data-cy="first-deposit-warning"
        >
          <LlIcon size="small" icon="mdi-alert" />
          {{ firstDepositMessage }}
        </span>
        <span v-else data-cy="deposit-retainer-note">
          This will record a retainer paid by the client into the trust account
        </span>
        <div
          v-if="firstDeposit && nameTooLong"
          class="text-yellow-600"
          data-cy="trust-deposit-qbo-warning"
        >
          <LlIcon size="small" icon="mdi-alert" />
          QuickBooks Online limits trust account names to 100 characters. This
          account will be created with a truncated name.
        </div>

        <div class="py-2">
          <div id="payment-method" class="flex">
            <div class="w-3/12">
              <!-- eslint-disable-next-line vuejs-accessibility/label-has-for -->
              <label for="deposit-payment-method" class="py-2"
                >Payment Method</label
              >
            </div>
            <div class="w-9/12">
              <LlSelect
                id="deposit-payment-method"
                v-model="formState.method"
                :items="paymentMethods"
                item-title="name"
                item-value="id"
                placeholder="Select Payment Method"
                variant="outlined"
                density="compact"
                hide-details="true"
                return-object
                :loading="methodsLoading"
                label-class="!hidden"
                data-cy="payment-method-select"
              />
            </div>
          </div>
        </div>

        <div class="flex items-center py-2">
          <div class="w-3/12">
            <!-- eslint-disable-next-line vuejs-accessibility/label-has-for -->
            <label for="deposit-date">Date</label>
          </div>
          <div class="w-4/12">
            <LlDatePicker
              id="deposit-date"
              v-model="formState.date"
              label=""
              label-class="!hidden"
              data-cy="date-picker"
              :show-button="false"
              :hide-details="true"
              @validation:error="uiState.hasDateError = $event"
            />
          </div>
          <div class="w-2/12 pr-3 text-right">
            <!-- eslint-disable-next-line vuejs-accessibility/label-has-for -->
            <label for="deposit-amount">Amount</label>
          </div>
          <div class="w-3/12">
            <LlTextField
              id="deposit-amount"
              :model-value="displayAmount"
              placeholder="0.00"
              data-cy="deposit-amount"
              required
              @input="onAmountInput"
              @focus="onAmountFocus"
              @blur="onAmountBlur"
            />
          </div>
        </div>

        <div class="flex py-2">
          <div class="w-3/12">
            <!-- eslint-disable-next-line vuejs-accessibility/label-has-for -->
            <label for="deposit-source" class="py-2">
              From
              <LlTooltip icon="help">
                Trust accounting rules require you to explicitly record the
                source of funds with all deposits
              </LlTooltip>
            </label>
          </div>
          <div class="w-9/12">
            <LlTextField
              id="deposit-source"
              v-model="formState.source"
              data-cy="deposit-source"
            />
          </div>
        </div>

        <div class="my-2 flex">
          <div class="w-3/12">
            <!-- eslint-disable-next-line vuejs-accessibility/label-has-for -->
            <label for="deposit-use" class="py-2">Use</label>
          </div>
          <div class="w-9/12">
            <LlTextField
              id="deposit-use"
              v-model="formState.description"
              data-cy="deposit-use"
            />
          </div>
        </div>

        <div class="my-2 flex">
          <div class="w-3/12">
            <!-- eslint-disable-next-line vuejs-accessibility/label-has-for -->
            <label for="deposit-memo" class="py-2">Memo</label>
          </div>
          <div class="w-9/12">
            <LlResizableTextarea
              id="deposit-memo"
              :model-value="memo"
              :rows="2"
              disabled
              data-cy="deposit-memo"
            />
          </div>
        </div>
      </v-card-text>

      <v-card-actions v-if="!uiState.loading" class="px-8 pb-6 pt-2">
        <LlSecondaryButton data-cy="deposit-cancel" @click="cancel"
          >Cancel</LlSecondaryButton
        >
        <v-spacer />
        <LlSecondaryButton
          id="record-deposit-button"
          color="primary"
          data-cy="record-deposit"
          :disabled="isSaveButtonDisabled"
          :loading="uiState.saving"
          @click="save"
        >
          Record Deposit
        </LlSecondaryButton>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { format } from 'date-fns';
import RouteService from '@/services/RouteService';
import type { UiRouterState } from '@/services/RouteService/UiRouterState';
import LlDatePicker from '@/components/LlDatePicker.vue';
import LlIcon from '@/components/LlIcon.vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import LlSelect from '@/components/LlSelect.vue';
import LlTooltip from '@/components/LlTooltip.vue';
import LlTextField from '@/components/LlTextField.vue';
import LlResizableTextarea from '@/components/LlResizableTextarea.vue';
import ClientMatterSearch from '@/components/ClientMatterSearch.vue';
import useClientMatterStore from '@/services/useClientMatterStore';
import { useMeStore } from '@/services/UserService/useMeStore';
import type { AccountDetail } from '@/services/UserService/accountDetail';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import {
  getOrConnectClient,
  getOrConnectMatter,
} from '@/services/accounting/accountingConnectService';
import {
  formatCurrencyInputDisplay,
  formatCurrencyInputTyping,
  parseCurrencyInput,
} from '@/services/utils';
import { reloadParentOnly } from '@/utils/routeHelpers';
import type {
  AccountingMethod,
  LooseRecord as Rec,
  TrustAccountSetting,
} from '@/models/trustDepositTypes';
import { useTrustDepositStore } from '@/stores/accounting/useTrustDepositStore';
import {
  buildDepositMemo,
  formatDepositError,
  isFirstDeposit,
  resolveTrustAccount,
  shouldOfferTrustAccount,
  trustAccountNameTooLong,
} from '@/utils/trustDepositHelpers';

const props = defineProps<{
  state?: UiRouterState;
  stateParams?: Record<string, any>;
  /** Supplied by iocContainer for the route mount; absent when a parent mounts this inline. */
  rootScope?: {
    $broadcast: (event: string) => { defaultPrevented: boolean };
  } | null;
  /** Mounted by a parent component rather than a route: leave through emits, never the router. */
  inline?: boolean;
  /**
   * SC-45762 — opened from the Settlement tab: hides the client/matter picker and names (the
   * matter is already in context) and pre-fills Use with "Settlement Funds".
   */
  settlementContext?: boolean;
}>();

const emit = defineEmits<{
  saved: [];
  cancel: [];
}>();

const meStore = useMeStore();
const clientMatterStore = useClientMatterStore();
const trustDepositStore = useTrustDepositStore();
const { paymentMethods, methodsLoading } = storeToRefs(trustDepositStore);
const { handleError, handleSuccess, handleWarning } = useSnackbarHandler();
const routeService = new RouteService(props);

const me = ref<AccountDetail | null>(null);
const trusts = ref<TrustAccountSetting[]>([]);

const uiState = reactive({
  loading: true,
  saving: false,
  pick: false,
  hasDateError: false,
});

/** Full client/matter records: billing, accounting and trust are read off these. */
const formState = reactive({
  client: null as Rec | null,
  matter: null as Rec | null,
  trust: undefined as TrustAccountSetting | undefined,
  method: null as (AccountingMethod & Rec) | null,
  date: new Date() as Date | null,
  amount: undefined as number | undefined,
  source: '',
  description: '',
});

/**
 * What <ClientMatterSearch> reads and writes: lean option records (id/label only). The watchers
 * below swap the full records into formState whenever the selection changes.
 */
const cm = reactive({ client: null as Rec | null, matter: null as Rec | null });
const cmQuery = ref<Rec | null>(null);
const displayAmount = ref('');
/** The account whose summary page opened the dialog; preselected until a record's own one applies. */
const routeTrustId = ref<string | undefined>();

const isClientLevel = computed(() => !!formState.client?.billing?.billClient);
const firstDeposit = computed(() =>
  isFirstDeposit(formState.client, formState.matter),
);
const nameTooLong = computed(() =>
  trustAccountNameTooLong(formState.client, formState.matter),
);
const showTrustAccount = computed(() =>
  shouldOfferTrustAccount(trusts.value, formState.client, formState.matter),
);
const memo = computed(() =>
  buildDepositMemo(formState.source, formState.description),
);
const firstDepositMessage = computed(() => {
  const subject = formState.matter ? me.value?.terms.matter : 'client';
  const accounts = isClientLevel.value ? 'account' : 'accounts';
  return `This is the first deposit for this ${subject} -- the necessary ${accounts} will be created in QuickBooks`;
});
const isSaveButtonDisabled = computed(
  () =>
    uiState.loading ||
    uiState.saving ||
    uiState.hasDateError ||
    !formState.date,
);

function updateTrustAccount() {
  formState.trust = resolveTrustAccount(
    trusts.value,
    formState.client,
    formState.matter,
    routeTrustId.value,
  );
}

/**
 * Give an unconnected client the firm's billing level, so the right accounts get created. Copies
 * rather than mutates: the record is the one useClientMatterStore caches for every other view.
 */
function setDefaultClientBilling() {
  const client = formState.client as Rec;
  if (client.accounting) return;
  if (typeof client.billing?.billClient === 'undefined') {
    formState.client = {
      ...client,
      billing: {
        ...client.billing,
        billClient: me.value?.company.settings?.clientBilling?.billClient,
      },
    };
  }
  if (formState.client?.billing.billClient) formState.matter = null;
}

async function loadFromRoute() {
  try {
    // useMeStore toasts its own failure and resolves `{}`; stop there rather than toast a TypeError.
    const loaded = await meStore.getMe();
    if (!loaded?.id) return;
    me.value = loaded;
    trusts.value = loaded.company.settings?.trust?.accounts ?? [];
    // All four role flags off is the /options equivalent of the old scope:'all' load. Pairing that
    // with our own account id keeps restricted-access matters we're shared on in the list; a null
    // scopeAccount would silently drop every one of them.
    cmQuery.value = {
      originating: false,
      involved: false,
      reviewer: false,
      primary: false,
      scopeAccount: loaded.id,
      archived: false,
      showSecondarySplitMatter: true,
    };

    const { id, clientId, matterId, client, matter, trustId } =
      routeService.params() ?? {};
    // The `.client.matter.*.deposit` states don't declare `clientId` — the client id arrives as
    // `id`, inherited from the parent `/client/{id}` url. `client` is an object param elsewhere;
    // its id is what /clients/{id} needs.
    const c = clientId || id || client?.id;
    const m = matterId || matter;
    // force: true throughout. The store caches one id at a time, and recording a deposit changes
    // billing.trust server-side — a cached record would re-show the first-deposit warning.
    const [routeMatter, routeClient] = await Promise.all([
      m ? clientMatterStore.getMatterById(m, true) : null,
      c ? clientMatterStore.getClientById(c, true) : null,
    ]);
    if (routeMatter) formState.matter = routeMatter;
    if (routeClient) {
      formState.client = routeClient;
    } else if (formState.matter?.client?.id) {
      // Only the matter is known (e.g. Settlement tab): derive the client, keep the picker usable.
      formState.client = await clientMatterStore.getClientById(
        formState.matter.client.id,
        true,
      );
      uiState.pick = true;
    }
    if (
      !formState.client ||
      (!formState.matter && formState.client.billing?.billClient)
    ) {
      uiState.pick = true;
    }

    routeTrustId.value = trustId;
    updateTrustAccount();

    if (props.settlementContext) {
      uiState.pick = false;
      if (!formState.description) formState.description = 'Settlement Funds';
    }

    // Seeding with the full records is safe: the picker swaps in its own lean ones, and the
    // id-keyed watchers see no change.
    cm.client = formState.client || null;
    cm.matter = formState.matter || null;
  } catch (err) {
    handleError(formatDepositError(err));
  } finally {
    uiState.loading = false;
  }
}

async function syncClient(clientId?: string) {
  if (!clientId) {
    formState.client = null;
    updateTrustAccount();
    return;
  }
  if (formState.client?.id === clientId) return;
  let client: Rec;
  try {
    client = await clientMatterStore.getClientById(clientId, true);
  } catch (err) {
    // A rejection for a client the user has already moved off must not wipe the one they see.
    if (cm.client?.id !== clientId) return;
    // Keeping the previous client would let save() post against one the picker no longer shows.
    formState.client = null;
    cm.client = null;
    handleError(formatDepositError(err));
    return;
  }
  // Nor may a late success: save() would post against a record the picker no longer shows.
  if (cm.client?.id !== clientId) return;
  formState.client = client;
  setDefaultClientBilling();
  if (isClientLevel.value) cm.matter = null;
  updateTrustAccount();
}

async function syncMatter(matterId?: string) {
  // The picker can auto-select a lone matter behind its hidden matter field; a client-level
  // deposit has no matter, so refuse it here.
  if (!matterId || isClientLevel.value) {
    formState.matter = null;
    updateTrustAccount();
    return;
  }
  if (formState.matter?.id === matterId) return;
  let matter: Rec;
  try {
    matter = await clientMatterStore.getMatterById(matterId, true);
  } catch (err) {
    if (cm.matter?.id !== matterId) return;
    formState.matter = null;
    cm.matter = null;
    handleError(formatDepositError(err));
    return;
  }
  if (cm.matter?.id !== matterId) return;
  formState.matter = matter;
  updateTrustAccount();
}

// Keyed on the id, so the picker re-assigning the same selection doesn't refetch.
watch(() => cm.client?.id, syncClient);
watch(() => cm.matter?.id, syncMatter);

// SC-45762: currency-formatted amount; the cursor is restored by hand as commas come and go.
function onAmountInput(e: Event) {
  const el = e.target as HTMLInputElement;
  const { formatted, newCursor } = formatCurrencyInputTyping(
    el.value,
    el.selectionEnd ?? el.value.length,
  );
  el.value = formatted;
  displayAmount.value = formatted;
  el.setSelectionRange(newCursor, newCursor);
  const parsed = parseCurrencyInput(formatted);
  // BUG-DEPOSIT-002: not rounded, as before the migration — fix separately.
  formState.amount = parsed ?? undefined;
}

function onAmountBlur() {
  displayAmount.value = formatCurrencyInputDisplay(formState.amount);
}

function onAmountFocus() {
  // Unformatted while editing, so typing doesn't fight the commas; empty shows the placeholder.
  displayAmount.value =
    formState.amount != null && formState.amount > 0
      ? String(formState.amount)
      : '';
}

function cancel() {
  if (props.inline) emit('cancel');
  else routeService.go('^');
}

/**
 * A parent that refreshes its own figures answers with preventDefault(), and we leave without
 * re-entering its state. Anything that does not answer gets the reload, so a mount with no
 * listener is never left showing a stale balance.
 */
function dismissAfterDeposit() {
  const handled = !!props.rootScope?.$broadcast('trust:depositRecorded')
    .defaultPrevented;
  routeService.go(
    '^',
    {},
    handled
      ? {}
      : {
          reload: reloadParentOnly(routeService.currentStateName()),
        },
  );
}

/** Thrown once connect() has told the user what to do; save() stops without a second toast. */
const CONNECT_REQUIRED = Symbol('connect required');

/**
 * No mount can show the QuickBooks customer picker: the route's `.connect` child has no view to
 * render into, and inline there is no child route at all. So every mount says where to do it.
 */
function connect(accounting: Rec, item: string) {
  if (!accounting.connect) return;
  handleWarning(
    `To record this deposit, the ${item} must first be connected to a QuickBooks customer. Please do this from the ${item} settings.`,
  );
  throw CONNECT_REQUIRED;
}

/**
 * Link the client/matter to a QuickBooks customer first, if it isn't yet. Rejects when the user
 * has to act, which is what stops save().
 */
function ensureConnected(): Promise<void> {
  const client = formState.client as Rec;
  const { matter } = formState;
  const meValue = me.value as AccountDetail;
  if (
    (!matter || matter.accounting || client.billing.billClient) &&
    client.accounting
  ) {
    return Promise.resolve();
  }
  // form records are freshly loaded (force), so they go straight through without a re-fetch.
  if (matter) {
    return getOrConnectMatter(matter, meValue).then((accounting) =>
      connect(accounting, meValue.terms.matter),
    );
  }
  return getOrConnectClient(client, meValue).then((accounting) =>
    connect(accounting, 'client'),
  );
}

function validationProblem(): string | null {
  const { amount, date, client, source, method } = formState;
  if (uiState.hasDateError) return 'Date is invalid';
  if (!client) return 'You have to select a client to record a deposit';
  // Before ensureConnected(), which would otherwise link the client in QuickBooks first.
  if (!formState.matter && !client.billing?.billClient) {
    return `You have to select a ${me.value?.terms.matter} to record a deposit`;
  }
  if (!(amount && amount > 0))
    return 'An amount is required to record a deposit';
  if (!date) return 'Date is required to record a deposit';
  if (!method) return 'Payment Method is required to record a deposit';
  if (!source) return "The 'From' field is required";
  return null;
}

async function save() {
  const problem = validationProblem();
  if (problem) {
    handleWarning(problem);
    return;
  }
  uiState.saving = true;
  try {
    await ensureConnected();
    const client = formState.client as Rec;
    const { matter, method, trust } = formState;
    const { billClient } = client.billing;
    await trustDepositStore.recordDeposit({
      level: billClient ? 'client' : 'matter',
      id: billClient ? client.id : (matter as Rec).id,
      body: {
        amount: formState.amount as number,
        date: format(formState.date as Date, 'yyyy-MM-dd'),
        description: memo.value,
        // BUG-DEPOSIT-001: unguarded, as before the migration — a missing account surfaces as a
        // raw TypeError. Pinned by jest/components/TrustDepositModal.spec.ts; fix separately.
        trust: trust!.id ? { id: trust!.id } : undefined,
        method: { id: method!.id, name: method!.name, type: method!.type },
      },
    });
    handleSuccess('The deposit was recorded in the client trust account');
    if (props.inline) emit('saved');
    else dismissAfterDeposit();
  } catch (err) {
    // A failed save is a warning, as it was before the migration; load failures stay errors.
    if (err !== CONNECT_REQUIRED) handleWarning(formatDepositError(err));
  } finally {
    uiState.saving = false;
  }
}

onMounted(() => {
  loadFromRoute();
  // No toast on failure, as before the migration: the list stays empty (the store keeps the
  // error in methodsError).
  trustDepositStore.loadPaymentMethods().catch(() => {});
});
</script>
